import { useManageCriteria } from '~/common/api';
import { useI18n, Trans } from '~/common/context';
import { useCommonErrorTranslation } from '~/common/hooks';
import { notify, useConfirmation } from '@wyz/ui';
import { ValidationErrorResponse } from '@wyz/types';
import { CommonErrorCodesEnumType } from '~/common/types';
import { CommonErrorCodes } from '~/common/helpers';
import { ICriteria, ICreateCriteriaForm } from '~/connect/types';

interface UseCriteriaActionsProps {
  criteriaData?: ICriteria[];
}

export const useCriteriaActions = ({
  criteriaData,
}: UseCriteriaActionsProps) => {
  const { t } = useI18n();
  const { resolveErrorKey } = useCommonErrorTranslation();
  const ask = useConfirmation();

  const manageCriteria = useManageCriteria({
    create: { enabled: true },
    remove: { enabled: true },
    update: { enabled: true },
  });

  const alertErrors = (
    error: ValidationErrorResponse<CommonErrorCodesEnumType>,
  ) => {
    error.errors.forEach(({ errors }) =>
      errors.forEach((error) => {
        notify.error(<Trans i18nKey={resolveErrorKey(error)} />, {
          id: 'toast-error-criteria-validation',
        });
      }),
    );
  };

  const handleToggleVisibility = (id: string, visible: boolean) => {
    const criteria = criteriaData?.find((c) => c.id === id);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id,
        body: {
          visible,
        },
      },
      {
        onSuccess: () => {
          notify.success(<Trans i18nKey='app.criteria.create.success' />, {
            id: 'toast-success-criteria-updated',
          });
        },
        onError: () => {
          notify.error(<Trans i18nKey='app.common.messages.error' />, {
            id: 'toast-error-criteria-visibility',
          });
        },
      },
    );
  };

  const handleEditCriteria = (id: string, newName: string) => {
    const criteria = criteriaData?.find((c) => c.id === id);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id,
        body: {
          ...criteria,
          name: newName,
        },
      },
      {
        onSuccess: () => {
          notify.success(<Trans i18nKey='app.common.messages.success' />, {
            id: 'toast-success-criteria-name-updated',
          });
        },
        onError: (error) => {
          notify.error(<Trans i18nKey='app.common.messages.error' />, {
            id: 'toast-error-criteria-name',
          });
          alertErrors(error);
        },
      },
    );
  };

  const handleDeleteCriteria = (id: string) => {
    const criteria = criteriaData?.find((c) => c.id === id);
    if (!criteria) return;

    ask({
      title: t('app.criteria.delete_criterion.confirmation.title', {
        criterion: criteria.name,
      }),
      description: t('app.criteria.delete_criterion.confirmation.description', {
        criterion: criteria.name,
      }),
      cancelBtnText: t('app.common.button.cancel'),
      confirmBtnText: t('app.common.buttons.delete'),
      confirmBtnVariant: 'danger',
    }).onConfirm(() => {
      manageCriteria.remove.mutate(id, {
        onSuccess: () =>
          notify.success(
            <Trans i18nKey='app.criteria.list.actions.delete.success' />,
            {
              id: 'toast-success-criteria-deleted',
            },
          ),
        onError: (error) => {
          if (error.message === CommonErrorCodes.ERR_CRITERION_IN_USE) {
            notify.error(
              <Trans
                i18nKey={resolveErrorKey(error.message)}
                values={{
                  countSettingTemplate: error.meta.SETTING_TEMPLATE,
                  countSupplierTemplate: error.meta.SUPPLIER_TEMPLATE,
                  countBrandTemplate: error.meta.BRAND_TEMPLATE,
                  countAdvert: error.meta.ADVERT,
                  countTheme: error.meta.THEME,
                  countQuoteBrandTemplate: error.meta.QUOTE_BRAND_TEMPLATE,
                  countRedistributionBrandTemplate:
                    error.meta.REDISTRIBUTION_BRAND_TEMPLATE,
                  countRedistributionSupplierTemplate:
                    error.meta.REDISTRIBUTION_SUPPLIER_TEMPLATE,
                  countWheelSupplierTemplate:
                    error.meta.WHEEL_SUPPLIER_TEMPLATE,
                  countS2sFlowTemplate: error.meta.S2S_FLOW_TEMPLATE,
                }}
              />,
              {
                id: 'toast-error-criteria-delete-in-use',
              },
            );
          } else {
            notify.error(
              <Trans i18nKey='app.criteria.list.actions.delete.error' />,
              {
                id: 'toast-error-criteria-delete-failed',
              },
            );
          }
        },
      });
    });
  };

  const handleUpdateValue = (
    criteriaId: string,
    valueIndex: number,
    newValue: string,
  ) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: criteria.values.map(
            (item: ICriteria['values'][number], _index: number) =>
              valueIndex !== _index ? item : { ...item, label: newValue },
          ),
        },
      },
      {
        onError: alertErrors,
      },
    );
  };

  const handleDeleteValue = (criteriaId: string, valueIndex: number) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: criteria.values.filter((_, _index) => valueIndex !== _index),
        },
      },
      {
        onError(error) {
          if (error.message === CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE) {
            notify.error(
              <Trans
                i18nKey={resolveErrorKey(error.message)}
                values={{
                  countCustomer: error.meta.CUSTOMER,
                  countSettingTemplate: error.meta.SETTING_TEMPLATE,
                  countSupplierTemplate: error.meta.SUPPLIER_TEMPLATE,
                  countBrandTemplate: error.meta.BRAND_TEMPLATE,
                  countAdvert: error.meta.ADVERT,
                  countTheme: error.meta.THEME,
                  countQuoteBrandTemplate: error.meta.QUOTE_BRAND_TEMPLATE,
                  countRedistributionBrandTemplate:
                    error.meta.REDISTRIBUTION_BRAND_TEMPLATE,
                  countRedistributionSupplierTemplate:
                    error.meta.REDISTRIBUTION_SUPPLIER_TEMPLATE,
                  countWheelSupplierTemplate:
                    error.meta.WHEEL_SUPPLIER_TEMPLATE,
                  countS2sFlowTemplate: error.meta.S2S_FLOW_TEMPLATE,
                }}
              />,
              {
                id: 'toast-error-criteria-value-delete-in-use',
              },
            );
          }
        },
      },
    );
  };

  const handleAddValue = (criteriaId: string, newValue: string) => {
    const criteria = criteriaData?.find((c) => c.id === criteriaId);
    if (!criteria) return;

    manageCriteria.update.mutate(
      {
        id: criteriaId,
        body: {
          ...criteria,
          values: [...criteria.values, { label: newValue }],
        },
      },
      {
        onError: alertErrors,
      },
    );
  };

  const handleCreateCriteria = (
    data: ICreateCriteriaForm,
    onSuccess?: () => void,
  ) => {
    manageCriteria.create.mutate(
      {
        name: data.name,
        values: data.values,
        visible: data.visible,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          notify.success(<Trans i18nKey='app.criteria.create.success' />, {
            id: 'toast-success-criteria-created',
          });
        },
        onError: alertErrors,
      },
    );
  };

  return {
    actions: {
      toggleVisibility: handleToggleVisibility,
      edit: handleEditCriteria,
      delete: handleDeleteCriteria,
      updateValue: handleUpdateValue,
      deleteValue: handleDeleteValue,
      addValue: handleAddValue,
      create: handleCreateCriteria,
    },
    isCreating: manageCriteria.create.isPending,
    isUpdating: manageCriteria.update.isPending,
  };
};
